import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Eye, EyeOff } from 'lucide-react';
import {
  incrementTick,
  selectLoginError,
  selectLoginIdentifier,
  selectLoginLockUntil,
  selectLoginPassword,
  selectLoginShowPassword,
  selectLoginSubmitting,
  selectLoginTick,
  setError,
  setIdentifier,
  setLockUntil,
  setPassword,
  setSubmitting,
  toggleShowPassword,
} from '../store/loginSlice.js';

const STORAGE_KEY_PREFIX = 'login_lock_until::'; // per identifier

function parseRemainingSeconds(message) {
  if (!message || typeof message !== 'string') return null;
  const match = message.match(/(\d+)\s*seconds?/i);
  if (!match) return null;
  const secs = parseInt(match[1], 10);
  return Number.isFinite(secs) ? secs : null;
}

function formatMMSS(totalSeconds) {
  const s = Math.max(0, totalSeconds || 0);
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(s % 60).padStart(2, '0');
  return `${mm}:${ss}`;
}

const LoginPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { login } = useAuth();
  const identifier = useSelector(selectLoginIdentifier);
  const password = useSelector(selectLoginPassword);
  const showPassword = useSelector(selectLoginShowPassword);
  const error = useSelector(selectLoginError);
  const submitting = useSelector(selectLoginSubmitting);
  const lockUntil = useSelector(selectLoginLockUntil);
  const tick = useSelector(selectLoginTick);

  // Load lock from localStorage when identifier changes
  useEffect(() => {
    const key = `${STORAGE_KEY_PREFIX}${(identifier || '').trim().toLowerCase()}`;
    const saved = localStorage.getItem(key);

    if (saved) {
      const until = Number(saved);
      if (Number.isFinite(until) && until > Date.now()) {
        dispatch(setLockUntil(until));
      } else {
        localStorage.removeItem(key);
        dispatch(setLockUntil(null));
      }
    } else {
      dispatch(setLockUntil(null));
    }

    // also clear error when switching users
    dispatch(setError(''));
  }, [dispatch, identifier]);

  const remainingSeconds = useMemo(() => {
    if (!lockUntil) return 0;
    return Math.max(0, Math.ceil((lockUntil - Date.now()) / 1000));
  }, [lockUntil, tick]);

  const isLocked = remainingSeconds > 0;

  // Countdown ticker while locked
  useEffect(() => {
    if (!lockUntil) return;

    const id = setInterval(() => {
      dispatch(incrementTick());
    }, 1000);

    return () => clearInterval(id);
  }, [dispatch, lockUntil]);

  // When countdown ends, unlock + clear persisted lock
  useEffect(() => {
    if (!lockUntil) return;
    if (remainingSeconds <= 0) {
      const key = `${STORAGE_KEY_PREFIX}${(identifier || '').trim().toLowerCase()}`;
      localStorage.removeItem(key);
      dispatch(setLockUntil(null));
      dispatch(setError(''));
    }
  }, [dispatch, remainingSeconds, lockUntil, identifier]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(setError(''));

    // Prevent clicking while locked (no backend calls)
    if (isLocked) {
      dispatch(setError(`Too many failed login attempts. Try again in ${remainingSeconds} seconds.`));
      return;
    }

    try {
      dispatch(setSubmitting(true));

      const user = await login(identifier, password);

      if (user) {
        const { role } = user;

        switch (role) {
          case 'Super Admin':
            navigate('/superadmin', { replace: true });
            break;
          case 'Admin':
            navigate('/admin', { replace: true });
            break;
          case 'Verifier':
            navigate('/verifier', { replace: true });
            break;
          case 'Collector':
            navigate('/collector', { replace: true });
            break;
          case 'Payment Redeemer':
            navigate('/payment-redeemer', { replace: true });
            break;
          case 'Payment Poster':
            navigate('/payment-poster', { replace: true });
            break;
          case 'Hearing Representative':
            navigate('/hearing-representative', { replace: true });
            break;
          case 'Provider':
            navigate('/provider', { replace: true });
            break;
          default:
            navigate('/dashboard', { replace: true });
        }
      }
    } catch (err) {
      const status = err?.status;
      const message = err?.message || 'Login failed. Please try again.';

      if (status === 429) {
        // If backend message includes remaining seconds, use it.
        const secs = parseRemainingSeconds(message) ?? 5 * 60; // fallback 5 minutes
        const until = Date.now() + secs * 1000;

        const key = `${STORAGE_KEY_PREFIX}${(identifier || '').trim().toLowerCase()}`;
        localStorage.setItem(key, String(until));

        dispatch(setLockUntil(until));
        dispatch(setError(typeof message === 'string' ? message : 'Too many attempts. Try again later.'));
      } else if (status === 401) {
  const attemptsLeft =
    err?.data?.attemptsLeft ?? err?.response?.data?.attemptsLeft;

  if (typeof attemptsLeft === 'number') {
    const msg = `Invalid username/email or password. Attempts left: ${attemptsLeft}`;
    dispatch(setError(msg));

    // OPTIONAL POPUP (uncomment if you want actual popup)
    // alert(msg);
  } else {
    dispatch(setError('Invalid username/email or password.'));
  }
}
 else {
        dispatch(setError(typeof message === 'string' ? message : 'Login failed. Please try again.'));
      }
    } finally {
      dispatch(setSubmitting(false));
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 relative p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-10 w-full max-w-md">
        <div className="flex justify-center mb-6">
          <img src="assets/logobar.png" alt="MedAxis Logo" className="h-14" />
        </div>

        <form onSubmit={handleSubmit}>
          {error && (
            <p className="text-red-500 text-sm mb-4">
              {error}
              {isLocked ? ` (Remaining: ${formatMMSS(remainingSeconds)})` : ''}
            </p>
          )}

          <div className="mb-4">
            <label htmlFor="identifier" className="block text-gray-700 text-sm font-semibold mb-2">
              Username or Email
            </label>
            <input
              id="identifier"
              type="text"
              placeholder="Enter your username or email"
              value={identifier}
              onChange={(e) => dispatch(setIdentifier(e.target.value))}
              required
              disabled={submitting}
              className="w-full border border-gray-300 rounded-lg py-2 px-3 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60"
            />
          </div>

          <div className="mb-6 relative">
            <label htmlFor="password" className="block text-gray-700 text-sm font-semibold mb-2">
              Password
            </label>
            <input
              id="password"
              type={showPassword ? 'text' : 'password'}
              placeholder="************"
              value={password}
              onChange={(e) => dispatch(setPassword(e.target.value))}
              required
              disabled={submitting}
              className="w-full border border-gray-300 rounded-lg py-2 px-3 pr-10 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:opacity-60"
            />
            <button
              type="button"
              onClick={() => dispatch(toggleShowPassword())}
              className="absolute right-3 top-9 text-gray-500 hover:text-gray-700"
              aria-label={showPassword ? 'Hide password' : 'Show password'}
            >
              {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
            </button>
          </div>

          <button
            type="submit"
            disabled={submitting || isLocked}
            className="w-full bg-[#1ca7ec] hover:bg-[#1a96d4] text-white font-semibold py-2 rounded-lg shadow-md transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {isLocked
              ? `Locked (${formatMMSS(remainingSeconds)})`
              : submitting
              ? 'Signing In...'
              : 'Sign In'}
          </button>
        </form>
      </div>

      <p
        className="absolute bottom-0 left-0 right-0 text-center text-xs text-gray-500"
        style={{ top: '83%', bottom: 'auto', transform: 'translateY(50%)' }}
      >
        ©{new Date().getFullYear()} · All Rights Reserved ·{' '}
        <a
          href="https://huburllc.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          Hubur Enterprises LLC.
        </a>{' '}
        Powered by{' '}
        <a
          href="https://theonellc.com/"
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:underline"
        >
          The One Services &amp; Solutions (TOSS)
        </a>
      </p>
    </div>
  );
};

export default LoginPage;
